import { Link, useLocation } from "react-router-dom";
import { useAdminSessionEmail } from "@/hooks/useAdminSessionEmail";
import { useAdminRole } from "@/hooks/useAdminRole";
import { formatAdminRoleLabel } from "@/features/auth/session/adminSessionRole";
import { useCustomerDetailBreadcrumb } from "@/features/customers/CustomerDetailBreadcrumbContext";
import { useAccountManagerProfileBreadcrumb } from "@/features/account-managers/AccountManagerProfileBreadcrumbContext";
import { getNewCustomerCreationResult } from "@/features/customers/new-customer/newCustomerCreationResult";
import { getNewCustomerWizardStep } from "@/features/customers/new-customer/newCustomerWizardSteps";
import { getAccountManagerById } from "@/features/account-managers/utils/getAccountManagerProfile";
import { getCustomerDetail } from "@/features/customers/utils/getCustomerDetail";
import { WizardStepHeaderProgress } from "@/features/customers/new-customer/WizardStepHeaderProgress";
import { AdminUserMenu } from "./AdminUserMenu";
import { layoutMessages } from "./messages";
import styles from "./AdminTopBar.module.css";

const PAGE_TITLES: Record<string, string> = {
  "/customers": layoutMessages.topBar.customers,
  "/onboarding-queue": layoutMessages.topBar.onboardingQueue,
  "/account-managers": layoutMessages.topBar.accountManagers,
  "/audit-log": layoutMessages.topBar.auditLog,
  "/settings": layoutMessages.topBar.settings,
};

function pageTitle(pathname: string): string {
  if (PAGE_TITLES[pathname]) {
    return PAGE_TITLES[pathname];
  }
  if (pathname.startsWith("/customers")) {
    return layoutMessages.topBar.customers;
  }
  if (pathname.startsWith("/account-managers")) {
    return layoutMessages.topBar.accountManagers;
  }
  return layoutMessages.topBar.admin;
}

function isAddAccountManagerPage(pathname: string): boolean {
  return pathname === "/account-managers/new";
}

function accountManagerProfileId(pathname: string): string | null {
  const match = pathname.match(/^\/account-managers\/([^/]+)$/);
  if (!match) {
    return null;
  }
  const id = decodeURIComponent(match[1]!);
  if (id === "new") {
    return null;
  }
  return id;
}

function customerCreatedBreadcrumbTitle(pathname: string): string | null {
  const match = pathname.match(/^\/customers\/([^/]+)\/created$/);
  if (!match) {
    return null;
  }
  const id = decodeURIComponent(match[1]!);
  const result = getNewCustomerCreationResult();
  if (result && result.customerId === id) {
    return result.customerName;
  }
  return layoutMessages.topBar.customerFallback;
}

type CustomerDetailNavState = {
  breadcrumbLabel?: string;
};

type AccountManagerProfileNavState = {
  breadcrumbLabel?: string;
};

function customerDetailOrganizationId(pathname: string): string | null {
  const match = pathname.match(/^\/customers\/([^/]+)$/);
  if (!match) {
    return null;
  }
  const id = decodeURIComponent(match[1]!);
  if (id === "new") {
    return null;
  }
  return id;
}

function resolveCustomerDetailBreadcrumbLabel(
  organizationId: string,
  options: {
    loadedLabel: string | null;
    navStateLabel?: string;
  },
): string | null {
  if (options.loadedLabel) {
    return options.loadedLabel;
  }
  if (options.navStateLabel) {
    return options.navStateLabel;
  }
  const detail = getCustomerDetail(organizationId);
  return detail?.name ?? detail?.breadcrumbLabel ?? null;
}

function newCustomerStep(pathname: string): number | null {
  const match = pathname.match(/\/customers\/new\/step-(\d)/);
  if (!match) {
    return null;
  }
  return Number.parseInt(match[1]!, 10);
}

function userDisplay(
  email: string,
  options: { fullName?: string; roleLabel: string },
) {
  if (!email && !options.fullName) {
    return {
      initials: "AD",
      name: "—",
      role: options.roleLabel,
    };
  }

  const displayName =
    options.fullName?.trim() ||
    (() => {
      const local = email.split("@")[0] ?? "";
      const parts = local.replace(/[._-]+/g, " ").trim().split(/\s+/);
      return parts
        .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
        .join(" ");
    })();

  const nameParts = displayName.trim().split(/\s+/).filter(Boolean);
  const initials =
    nameParts.length >= 2
      ? `${nameParts[0]![0] ?? ""}${nameParts[1]![0] ?? ""}`.toUpperCase()
      : (nameParts[0] ?? email).slice(0, 2).toUpperCase();

  return {
    initials: initials || "AD",
    name: displayName || email || "—",
    role: options.roleLabel,
  };
}

export function AdminTopBar() {
  const { pathname, state: locationState } = useLocation();
  const sessionEmail = useAdminSessionEmail();
  const { role, fullName } = useAdminRole();
  const { organizationId: loadedOrgId, label: loadedLabel } =
    useCustomerDetailBreadcrumb();
  const { managerId: loadedManagerId, label: loadedManagerLabel } =
    useAccountManagerProfileBreadcrumb();
  const currentPage = pageTitle(pathname);
  const user = userDisplay(sessionEmail, {
    fullName,
    roleLabel: formatAdminRoleLabel(role),
  });
  const wizardStep = newCustomerStep(pathname);
  const wizardStepInfo = getNewCustomerWizardStep(pathname);
  const createdCustomerTitle = customerCreatedBreadcrumbTitle(pathname);
  const isNewCustomerFlow =
    wizardStep !== null || createdCustomerTitle !== null;
  const detailOrganizationId = customerDetailOrganizationId(pathname);
  const navStateLabel = (locationState as CustomerDetailNavState | null)
    ?.breadcrumbLabel;
  const profileNavStateLabel = (
    locationState as AccountManagerProfileNavState | null
  )?.breadcrumbLabel;
  const detailCustomerLabel =
    detailOrganizationId !== null
      ? resolveCustomerDetailBreadcrumbLabel(detailOrganizationId, {
          loadedLabel:
            loadedOrgId === detailOrganizationId ? loadedLabel : null,
          navStateLabel,
        })
      : null;
  const isAddAccountManager = isAddAccountManagerPage(pathname);
  const profileManagerId = accountManagerProfileId(pathname);
  const profileManagerName = profileManagerId
    ? (loadedManagerId === profileManagerId
        ? loadedManagerLabel
        : profileNavStateLabel ??
          getAccountManagerById(profileManagerId)?.name) ?? null
    : null;

  return (
    <header
      className={`${styles.bar} ${wizardStepInfo ? styles.barWithWizard : ""}`}
      data-testid="admin-top-bar"
    >
      <nav
        className={styles.breadcrumbs}
        aria-label={layoutMessages.topBar.breadcrumbAriaLabel}
      >
        {detailOrganizationId !== null ? (
          <>
            <Link to="/customers" className={styles.crumbLink}>
              {layoutMessages.topBar.customers}
            </Link>
            {detailCustomerLabel ? (
              <>
                <span className={styles.crumbSep} aria-hidden>
                  ›
                </span>
                <span className={styles.crumbCurrent}>
                  <strong className={styles.crumbStrong}>
                    {detailCustomerLabel}
                  </strong>
                </span>
              </>
            ) : null}
          </>
        ) : isAddAccountManager ? (
          <>
            <Link to="/account-managers" className={styles.crumbLink}>
              {layoutMessages.topBar.accountManagers}
            </Link>
            <span className={styles.crumbSep} aria-hidden>
              ›
            </span>
            <span className={styles.crumbCurrent}>
              <strong className={styles.crumbStrong}>
                {layoutMessages.topBar.addNew}
              </strong>
            </span>
          </>
        ) : profileManagerId !== null ? (
          <>
            <Link to="/account-managers" className={styles.crumbLink}>
              {layoutMessages.topBar.accountManagers}
            </Link>
            {profileManagerName ? (
              <>
                <span className={styles.crumbSep} aria-hidden>
                  ›
                </span>
                <span className={styles.crumbCurrent}>
                  <strong className={styles.crumbStrong}>
                    {profileManagerName}
                  </strong>
                </span>
              </>
            ) : null}
          </>
        ) : (
          <>
            {!isNewCustomerFlow && !isAddAccountManager ? (
              <>
                <Link to="/customers" className={styles.crumbLink}>
                  {layoutMessages.topBar.adminPortal}
                </Link>
                <span className={styles.crumbSep} aria-hidden>
                  ›
                </span>
              </>
            ) : null}
            {wizardStep !== null ? (
              <>
                <Link to="/customers" className={styles.crumbLink}>
                  {layoutMessages.topBar.customers}
                </Link>
                <span className={styles.crumbSep} aria-hidden>
                  ›
                </span>
                <span className={styles.crumbCurrent}>
                  <strong className={styles.crumbStrong}>
                    {layoutMessages.topBar.newCustomer}
                  </strong>
                  {" · "}
                  {layoutMessages.topBar.stepOf(wizardStep)}
                </span>
              </>
            ) : createdCustomerTitle !== null ? (
              <>
                <Link to="/customers" className={styles.crumbLink}>
                  {layoutMessages.topBar.customers}
                </Link>
                <span className={styles.crumbSep} aria-hidden>
                  ›
                </span>
                <span className={styles.crumbCurrent}>
                  <strong className={styles.crumbStrong}>{createdCustomerTitle}</strong>
                  {" · "}
                  {layoutMessages.topBar.createdCustomerStep(4)}
                </span>
              </>
            ) : (
              <span className={styles.crumbCurrent}>{currentPage}</span>
            )}
          </>
        )}
      </nav>
      <div className={styles.right}>
        <AdminUserMenu user={user} />
      </div>
      {wizardStepInfo ? (
        <WizardStepHeaderProgress wizardStep={wizardStepInfo} />
      ) : null}
    </header>
  );
}
